<template>
  <div class="p-4 w-full">
    <!-- Header -->
    <div class="mb-6">
      <h1 class="text-xl md:text-2xl lg:text-3xl font-bold mb-4 text-white">
        {{ $t("user_manage.management_title") }}
      </h1>
    </div>

    <!-- Statistics Cards -->
    <UserStatsCards :users-stats="usersStats" />

    <!-- Tab Navigation -->
    <TabNavigation
      :current-tab="currentTab"
      :tabs="tabs"
      @select-tab="selectTab"
      @open-invite-modal="openInviteModal"
      @open-add-modal="openAddMemberModal"
      @open-new-service-modal="openNewServiceModal"
      @open-new-group-modal="openNewGroupModal"
    />

    <!-- Search Field -->
    <SearchField v-model="searchQuery" />

    <!-- Content Area -->
    <div class="mt-8">
      <!-- Users Tab -->
      <UsersDataTable
        v-if="currentTab === 'users'"
        :users="filteredUsers"
        :selected-users="selectedUsers"
        :is-loading="isLoading"
        :filters="filters"
        :page-size="pageSize"
        :users-skeleton="usersSkeleton"
        v-model:selectedUsers="selectedUsers"
        v-model:filters="filters"
        @edit-user="editUser"
        @manager-access="editUserRole"
        @delete-user="deleteUser"
        @toggle-status="toggleUserStatus"
        @change-service="changeUserService"
        @resend-invitation="resendInvitation"
      />

      <!-- Services Tab -->
      <ServicesDataTable
        v-if="currentTab === 'services'"
        :services="filteredServices"
        :is-loading="isLoading"
        @edit-service="editService"
        @delete-service="deleteService"
      />

      <!-- Groups Tab -->
      <GroupsDataTable
        v-if="currentTab === 'groups'"
        :groups="filteredGroups"
        :user-list="userList"
        :is-loading="isLoading"
        @edit-group="editGroup"
        @delete-group="deleteGroup"
      />
    </div>

    <!-- Modals -->
    <InviteUserModal
      v-model:visible="showInviteModal"
      :services="filteredServices"
      @save="handleHereInvitation"
    />

    <AddMemberModal
      v-model:visible="showAddMemberModal"
      :services="filteredServices"
      @save="handleAddMember"
    />

    <!-- Add this EditUserModal -->
    <EditUserModal
      v-model:visible="editUserModal"
      :user-data="updateDataPayload"
      :services="services"
      :selected-service-id="userUpdateServiceId"
      @save="saveUserChanges"
      @close="closeEditModal"
    />

    <ServiceModal
      v-model:visible="showServiceModal"
      :service-form="serviceForm"
      :is-editing="isEditingService"
      :users="userList"
      @save="saveService"
    />

    <GroupModal
      v-model:visible="showGroupModal"
      :group-form="groupForm"
      :is-editing="isEditingGroup"
      :users="userList"
      @save="saveGroup"
    />

    <!-- Global Components -->
    <ConfirmDialog>
      <template #container="{ message, acceptCallback, rejectCallback }">
        <div
          class="max-w-lg flex flex-col items-center p-8 bg-surface-0 dark:bg-surface-900 rounded"
        >
          <div
            class="rounded-full bg-primary text-primary-contrast inline-flex justify-center items-center h-24 w-24 -mt-20"
          >
            <i class="pi pi-question !text-4xl"></i>
          </div>
          <span class="font-bold text-2xl block mb-2 mt-6">{{
            message.header
          }}</span>
          <p class="mb-0">{{ message.message }}</p>

          <!-- Added input field for confirmation -->
          <div class="w-full mt-4" v-if="!isConfirmUpdateRole">
            <InputText
              v-model="confirmText"
              class="w-full"
              :placeholder="comfirmPlaceholder"
            />
          </div>

          <div class="flex items-center justify-between mt-6 w-full">
            <Button
              :label="$t('common.okay')"
              @click="acceptCallback"
              class="spect-btn-primary"
              size="small"
              :disabled="!isConfirmUpdateRole && confirmText !== confirmValue"
            ></Button>
            <Button
              :label="$t('common.cancel')"
              outlined
              @click="rejectCallback"
              class="spect-btn-outline"
              size="small"
            ></Button>
          </div>
        </div>
      </template>
    </ConfirmDialog>
    <Toast />
  </div>
</template>

<script setup>
/* eslint-disable */
import { ref, computed, onMounted } from "vue";
import { useI18n } from "vue-i18n";

// PrimeVue Components
import ConfirmDialog from "primevue/confirmdialog";
import Toast from "primevue/toast";
import InputText from "primevue/inputtext";
import Button from "primevue/button";

// Composables
import { useUserManagement } from "@/composables/useUserManagement";
import { useServiceManagement } from "@/composables/useServiceManagement";
import { useGroupManagement } from "@/composables/useGroupManagement";
import { useEmailInvitation } from "@/composables/useEmailInvitation";

// Components
import UserStatsCards from "@/components/user-management/UserStatsCards.vue";
import TabNavigation from "@/components/user-management/TabNavigation.vue";
import SearchField from "@/components/user-management/SearchField.vue";
import UsersDataTable from "@/components/user-management/UsersDataTable.vue";
import ServicesDataTable from "@/components/user-management/ServicesDataTable.vue";
import GroupsDataTable from "@/components/user-management/GroupsDataTable.vue";
import InviteUserModal from "@/components/user-management/InviteUserModal.vue";
import AddMemberModal from "@/components/user-management/AddMemberModal.vue";
import ServiceModal from "@/components/user-management/ServiceModal.vue";
import GroupModal from "@/components/user-management/GroupModal.vue";

// Add EditUserModal import
import EditUserModal from "@/components/user-management/EditUserModal.vue";
import { getUsersStats } from "@/services/userManagementService";

const { t } = useI18n();

const confirmText = ref("");

// Use composables
const {
  users,
  usersStats,
  loadUsersStats,
  isLoading,
  selectedUsers,
  selectedUser,
  isDeleteUser,
  isConfirmUpdateRole,
  filters,
  usersSkeleton,
  editUserModal,
  editingUser,
  userUpdateServiceId,
  updateDataPayload,
  loadUsers,
  deleteUser,
  editUserRole,
  toggleUserStatus,
  editUser,
  changeUserService,
  resendInvitation,
  addNewUser,
  saveUserChanges,
  closeEditModal,
} = useUserManagement();

// Update the script section to include the new reactive variables
const {
  services,
  serviceForm,
  isEditingService,
  showServiceModal,
  selectedService,
  isDeleteService,
  loadServices,
  saveService,
  editService,
  deleteService,
  openNewServiceModal,
} = useServiceManagement();

const {
  groups,
  groupForm,
  isEditingGroup,
  showGroupModal,
  selectedGroup,
  isDeleteGroup,
  loadGroups,
  saveGroup,
  editGroup,
  deleteGroup,
  openNewGroupModal,
} = useGroupManagement();

// Add email invitation composable
const { showInviteModal, handleInvitation, openInviteModal } =
  useEmailInvitation();

// Local reactive data
const currentTab = ref("users");
const searchQuery = ref(null);
const pageSize = ref(10);
const showAddMemberModal = ref(false);
// Remove showInviteModal as it's now from composable

// Computed properties
const tabs = computed(() => [
  { key: "users", label: t("user_manage.users") },
  { key: "services", label: t("user_manage.services") },
  { key: "groups", label: t("user_manage.groups") },
]);

// Update the computed properties to handle all deletion types
const comfirmPlaceholder = computed(() => {
  if (isDeleteUser.value) {
    return t("user_manage.confirm_user_deletion");
  } else if (isDeleteService.value) {
    return t("user_manage.confirm_service_deletion");
  } else if (isDeleteGroup.value) {
    return t("user_manage.confirm_group_deletion");
  }
});

const confirmValue = computed(() => {
  if (isDeleteUser.value) {
    return selectedUser.value?.email;
  } else if (isDeleteService.value) {
    return selectedService.value?.name?.split("_")[0];
  } else if (isDeleteGroup.value) {
    return selectedGroup.value?.name;
  }
});

/*const filteredUsers = computed(() => {
  if (!searchQuery.value) return users.value
  return users.value.filter(user => 
    user.firstname?.toLowerCase().includes(searchQuery.value.toLowerCase()) ||
    user.lastname?.toLowerCase().includes(searchQuery.value.toLowerCase()) ||
    user.email?.toLowerCase().includes(searchQuery.value.toLowerCase())
  )
})*/

const filteredServices = computed(() => {
  // Add null/undefined check before using slice
  if (!services.value || !Array.isArray(services.value)) return [];
  if (!searchQuery.value) return services.value;
  return services.value.filter((service) =>
    service.name?.toLowerCase().includes(searchQuery.value.toLowerCase())
  );
});

const filteredGroups = computed(() => {
  // Add similar protection for groups
  if (!groups.value || !Array.isArray(groups.value)) return [];
  if (!searchQuery.value) return groups.value;
  return groups.value.filter((group) =>
    group.name?.toLowerCase().includes(searchQuery.value.toLowerCase())
  );
});

const filteredUsers = computed(() => {
  // Add similar protection for users
  console.log(users.value);
  if (!users.value || !Array.isArray(users.value)) return [];
  console.log("true here");
  if (!searchQuery.value) return users.value;
  return users.value.filter(
    (user) =>
      user.firstname?.toLowerCase().includes(searchQuery.value.toLowerCase()) ||
      user.lastname?.toLowerCase().includes(searchQuery.value.toLowerCase()) ||
      user.email?.toLowerCase().includes(searchQuery.value.toLowerCase())
  );
});

const userList = computed(() => users.value);

// Methods
const selectTab = (tabKey) => {
  currentTab.value = tabKey;
};

// Remove openInviteModal as it's now from composable

const openAddMemberModal = () => {
  showAddMemberModal.value = true;
};

// Remove handleInvitation as it's now from composable

const handleAddMember = (payload) => {
  console.log("Add member payload:", payload);
  addNewUser(payload);
};

const handleHereInvitation = async (data) => {
  const response = await handleInvitation(data);
  console.log(response);
  if (response) {
    await loadUsers();
    await loadUsersStats();
    await loadServices();
  }
};

const loadData = async () => {
  try {
    await Promise.all([
      loadUsers(),
      loadServices(),
      loadGroups(),
      loadUsersStats(),
    ]);
  } catch (error) {
    console.error("Error loading data:", error);
  }
};

// Lifecycle
onMounted(async () => {
  await loadData();
});
</script>

<style lang="scss" scoped>
@use "../../../assets/scss/variables.scss";
@use "../../../assets/scss/app/common.scss";
</style>
